import React from "react";

export default function StepTwoPlaceQuestions({
  addressName,
  fullAddress,
  questions,
  answers,
  onAnswerSelect,
  onMultiAnswerToggle,
  onExtraTextChange,
  onAnswerText,
  onTimeChange,
  onBack,
  onNext,
  onEditLocation,
  saving,
}) {
  const renderExtraInput = (question, option) => {
    if (!option.requires_input) return null;
    const value = answers[question.id]?.extra_texts?.[option.id] || "";
    return (
      <textarea
        className="form-control mt-2 rtl-input"
        rows="2"
        placeholder={`اكتب ${option.label}...`}
        value={value}
        onChange={(e) =>
          onExtraTextChange(question.id, option.id, e.target.value)
        }
        onClick={(e) => e.stopPropagation()}
      />
    );
  };

  const renderTimeRange = (question) => {
    const value = answers[question.id]?.answer_value || {};
    return (
      <div className="d-flex gap-2 mt-2">
        <div className="flex-fill">
          <label className="small text-muted d-block mb-1">من</label>
          <input
            type="time"
            className="form-control"
            value={value.from || ""}
            onChange={(e) => onTimeChange(question.id, "from", e.target.value)}
          />
        </div>
        <div className="flex-fill">
          <label className="small text-muted d-block mb-1">إلى</label>
          <input
            type="time"
            className="form-control"
            value={value.to || ""}
            onChange={(e) => onTimeChange(question.id, "to", e.target.value)}
          />
        </div>
      </div>
    );
  };

  // الأسئلة التابعة (conditions) اللي المفروض تظهر تحت الـ option دي لو هي مختارة
  const renderChildQuestions = (option) => {
    if (!option.conditions?.length) return null;
    const children = option.conditions.filter(
      (c) => c.action === "show" && c.target_question,
    );
    if (!children.length) return null;

    return (
      <div className="nested-question mt-3 ps-3 border-start">
        {children.map((cond) => (
          <div key={`cond-${cond.id}`} className="mb-3">
            {renderQuestionBody(cond.target_question)}
          </div>
        ))}
      </div>
    );
  };

  const renderSingleChoice = (q) => {
    const selectedId = answers[q.id]?.question_option_id;
    return (
      <div className="wizard-options-grid">
        {q.options.map((opt) => {
          const isSelected = selectedId === opt.id;
          return (
            <div key={opt.id}>
              <div
                className={`wizard-option-chip cursor ${isSelected ? "selected" : ""}`}
                onClick={() => onAnswerSelect(q.id, opt.id)}
              >
                {opt.label}
              </div>
              {isSelected && renderExtraInput(q, opt)}
              {isSelected && renderChildQuestions(opt)}
            </div>
          );
        })}
      </div>
    );
  };

  const renderMultipleChoice = (q) => {
    const selectedIds = answers[q.id]?.question_option_ids || [];
    return (
      <div className="wizard-options-grid">
        {q.options.map((opt) => {
          const isSelected = selectedIds.includes(opt.id);
          return (
            <div key={opt.id}>
              <div
                className={`wizard-option-chip cursor ${isSelected ? "selected" : ""}`}
                onClick={() => onMultiAnswerToggle(q, opt)}
              >
                {opt.label}
              </div>
              {isSelected && renderExtraInput(q, opt)}
              {isSelected && renderChildQuestions(opt)}
            </div>
          );
        })}
      </div>
    );
  };

  // جسم أي سؤال (رئيسي أو تابع). indexLabel اختياري - للأسئلة الرئيسية بس
  const renderQuestionBody = (q, indexLabel) => {
    return (
      <React.Fragment>
        <div className="fw-medium mb-2 d-flex align-items-center gap-1 justify-content-end">
          <p className="mb-0">{q.title}</p>
          {indexLabel != null && <p className="mb-0">. {indexLabel}</p>}
        </div>

        {q.type === "single_choice" && renderSingleChoice(q)}
        {q.type === "multiple_choice" && renderMultipleChoice(q)}
        {q.type === "time" && renderTimeRange(q)}
        {q.type !== "single_choice" &&
          q.type !== "multiple_choice" &&
          q.type !== "time" && (
            <textarea
              className="form-control"
              rows="2"
              value={answers[q.id]?.answer_value || ""}
              onChange={(e) => onAnswerText(q.id, e.target.value)}
            />
          )}
      </React.Fragment>
    );
  };

  return (
    <div className="row">
      <div className="col-lg-8">
        <div className="wizard-card p-4">
          <p className="fw-semibold fs-5 mb-1">تفاصيل المكان</p>
          <p className="text-muted small mb-4">
            ادخل تفاصيل المكان لتسهيل وصول فريق الخدمة
          </p>

          {questions.map((q, idx) => (
            <div key={q.id} className="mb-4">
              {renderQuestionBody(q, idx + 1)}
            </div>
          ))}
        </div>
      </div>

      <div className="col-lg-4">
        <div className="wizard-card p-3 mb-3">
          <p className="fw-semibold small mb-2">معاينة العنوان</p>
          <div className="d-flex align-items-start gap-2">
            <i className="fa-solid fa-location-dot text-dark mt-1"></i>
            <div>
              <p className="fw-medium mb-0">{addressName}</p>
              <p className="text-muted small mb-1">{fullAddress}</p>
              <span
                className="wizard-link-btn small cursor"
                onClick={onEditLocation}
              >
                تعديل الموقع
              </span>
            </div>
          </div>
        </div>

        <div className="wizard-card p-3 mb-3">
          <p className="fw-semibold small mb-2">الخطوات</p>
          <div className="d-flex align-items-center gap-2 mb-2">
            <i className="fa-solid fa-circle-check text-success"></i>
            <span className="small text-muted">تحديد الموقع</span>
          </div>
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="wizard-mini-step active">2</span>
            <span className="small fw-medium">تفاصيل المكان</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="wizard-mini-step">3</span>
            <span className="small text-muted">مراجعة وحفظ</span>
          </div>
        </div>

        <div className="wizard-help-box p-3">
          <p className="fw-semibold small mb-2">تحتاج مساعدة؟</p>
          <p className="text-muted small mb-2">تواصل مع فريق الدعم</p>
          <button className="btn wizard-btn-dark w-100">الذهاب للدعم</button>
        </div>
      </div>

      <div className="col-12 d-flex justify-content-between mt-4">
        <button className="btn wizard-btn-outline" onClick={onBack}>
          <i className="fa-solid fa-arrow-right me-1"></i> السابق
        </button>
        <button
          className="btn wizard-btn-dark"
          onClick={onNext}
          disabled={saving}
        >
          {saving ? "جاري الحفظ..." : "التالي: مراجعة وحفظ"}{" "}
          <i className="fa-solid fa-arrow-left ms-1"></i>
        </button>
      </div>
    </div>
  );
}
